import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, RadialBarChart, RadialBar, Tooltip, CartesianGrid } from "recharts";
import { TrendingUp, AlertTriangle, DollarSign, Target, Gauge, ChevronRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import PageHeader from "@/components/PageHeader";
import { RiskBadge } from "@/components/StatusBadge";
import { useLang } from "@/context/LangContext";
import api from "@/lib/api";
import { fmtMoney } from "@/lib/format";

const LAYER_KEYS = ["model", "system", "operation", "absorption", "outcome"];
const LAYER_COLORS = ["#4338CA", "#0ea5e9", "#10b981", "#f59e0b", "#ec4899"];

function Kpi({ icon: Icon, label, value, accent, testId }) {
  return (
    <Card className="p-5 rounded-2xl shadow-sm border-border/60" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
          <p className="text-2xl font-bold mt-2 truncate">{value}</p>
        </div>
        <div className={`flex h-10 w-10 items-center justify-center rounded-xl shrink-0 ${accent}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  useEffect(() => { api.get("/dashboard/stats").then(({ data }) => setStats(data)); }, []);

  if (!stats) return <div className="h-64 flex items-center justify-center"><div className="h-8 w-8 rounded-full border-4 border-primary/30 border-t-primary animate-spin" /></div>;

  const domainData = Object.entries(stats.byDomain).map(([k, v]) => ({ name: k, value: v }));
  const layerData = LAYER_KEYS.map((k, i) => ({ name: t(k), value: stats.layerAvg[k], fill: LAYER_COLORS[i] }));
  const hiddenData = Object.entries(stats.hiddenCost).map(([k, v]) => ({ name: k, value: v }));

  const getContext = () => ({
    portfolioValue: stats.portfolioValue, realisedValue: stats.realisedValue, roi: stats.roi,
    highRiskCount: stats.highRiskCount, layerAvg: stats.layerAvg, avgAdoption: stats.avgAdoption,
    absorptionAvg: stats.layerAvg.absorption, hiddenCostTop: "Absorption",
  });

  return (
    <div className="space-y-6 fade-up" data-testid="dashboard-page">
      <PageHeader title={t("dashboard")} subtitle={t("use_cases_count")} screen="dashboard" getContext={getContext} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi testId="kpi-portfolio" icon={DollarSign} label={t("portfolio_value")} value={fmtMoney(stats.portfolioValue)} accent="bg-primary/10 text-primary" />
        <Kpi testId="kpi-realised" icon={TrendingUp} label={t("realised_value")} value={fmtMoney(stats.realisedValue)} accent="bg-emerald-500/10 text-emerald-600" />
        <Kpi testId="kpi-roi" icon={Gauge} label={t("roi")} value={`${stats.roi}%`} accent="bg-accent/15 text-amber-600" />
        <Kpi testId="kpi-risk" icon={AlertTriangle} label={t("high_risk")} value={stats.highRiskCount} accent="bg-rose-500/10 text-rose-600" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-5 sm:p-6 rounded-2xl shadow-sm">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            <h3 className="text-lg font-semibold">{t("hidden_cost")}</h3>
          </div>
          <p className="text-sm text-muted-foreground mb-4">{t("hidden_cost_desc")}</p>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={hiddenData} layout="vertical" margin={{ left: 10, right: 20 }}>
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <Tooltip formatter={(v) => fmtMoney(v)} cursor={{ fill: "hsl(var(--secondary))" }} />
              <Bar dataKey="value" radius={[0, 8, 8, 0]}>
                {hiddenData.map((e, i) => <Cell key={i} fill={e.name === "Absorption" ? "#f59e0b" : "#4338CA"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-5 sm:p-6 rounded-2xl shadow-sm">
          <h3 className="text-lg font-semibold mb-1">{t("layer_readiness")}</h3>
          <ResponsiveContainer width="100%" height={220}>
            <RadialBarChart innerRadius="30%" outerRadius="100%" data={layerData} startAngle={90} endAngle={-270}>
              <RadialBar background dataKey="value" cornerRadius={8} />
              <Tooltip formatter={(v) => `${v}%`} />
            </RadialBarChart>
          </ResponsiveContainer>
          <div className="grid grid-cols-2 gap-2 mt-2">
            {layerData.map((l) => (
              <div key={l.name} className="flex items-center gap-2 text-xs">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: l.fill }} />
                <span className="text-muted-foreground truncate">{l.name}</span>
                <span className="ms-auto font-semibold">{l.value}%</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card className="p-5 sm:p-6 rounded-2xl shadow-sm">
        <h3 className="text-lg font-semibold mb-4">{t("usecases_by_domain")}</h3>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={domainData} margin={{ bottom: 40 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
            <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-25} textAnchor="end" interval={0} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
            <Tooltip cursor={{ fill: "hsl(var(--secondary))" }} />
            <Bar dataKey="value" fill="#4338CA" radius={[8, 8, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      <div className="grid lg:grid-cols-2 gap-6">
        {[["top_roi", stats.topRoi, "roi"], ["critical_priority", stats.criticalPriority, "risk"]].map(([titleKey, rows, type]) => (
          <Card key={titleKey} className="p-5 sm:p-6 rounded-2xl shadow-sm" data-testid={`table-${titleKey}`}>
            <h3 className="text-lg font-semibold mb-4">{t(titleKey)}</h3>
            <div className="space-y-2">
              {rows.map((c) => (
                <button key={c.uid} onClick={() => navigate(`/use-cases/${c.uid}`)}
                  data-testid={`dash-row-${c.uid}`}
                  className="w-full flex items-center gap-3 rounded-xl border p-3 text-start hover:bg-secondary transition-colors">
                  <span className="text-xs font-mono text-muted-foreground shrink-0">{c.uid}</span>
                  <span className="text-sm font-medium truncate flex-1">{lang === "ar" ? c.name_ar : c.name}</span>
                  {type === "roi"
                    ? <span className="text-sm font-bold text-emerald-600 shrink-0">{c.roi}%</span>
                    : <RiskBadge risk={c.riskLevel} />}
                  <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 rtl:rotate-180" />
                </button>
              ))}
            </div>
          </Card>
        ))}
      </div>

      <Card className="p-5 sm:p-6 rounded-2xl bg-primary text-primary-foreground shadow-md">
        <p className="text-sm sm:text-base font-semibold leading-relaxed text-center">"{t("principle")}"</p>
      </Card>
    </div>
  );
}
